// Photos from the field, on the card (Ryan, Sep 29 2026: "people are uploading pictures
// and they're not being seen on the loops cards or the work order cards"). The phone's
// photo upload files pictures as job NOTES, which only the job page's Notes tile showed;
// loop-location and T&M-day photos live in their own tables. This strip shows every
// picture on the job in one place — newest first, who took it, what they said — with a
// tap-to-enlarge viewer. GET /projects/:id/photos gathers the three sources.
import React, { useEffect, useState } from 'react';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';

const when = iso => {
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const SOURCE = { note: 'Field photo', tracker: 'T&M day', loop: 'Loop location', hole: 'Drill hole', card: 'Card photo', co: 'Change order' };

export function useJobPhotos(projectId) {
  const [photos, setPhotos] = useState(null);
  const load = React.useCallback(() => {
    if (!projectId) return;
    apiFetch(`/projects/${projectId}/photos`).then(d => setPhotos(Array.isArray(d) ? d : [])).catch(() => setPhotos([]));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  return { photos, reload: load };
}

function Viewer({ photos, index, onIndex, onClose }) {
  const p = photos[index];
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && index < photos.length - 1) onIndex(index + 1);
      else if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [index, photos.length, onIndex, onClose]);
  if (!p) return null;
  const nav = (label, to, ok) => <button onClick={e => { e.stopPropagation(); if (ok) onIndex(to); }} disabled={!ok} style={{ width: 40, height: 40, borderRadius: 999, border: 'none', background: ok ? 'rgba(255,255,255,0.18)' : 'transparent', color: ok ? '#fff' : 'rgba(255,255,255,0.3)', fontSize: 22, fontWeight: 800, cursor: ok ? 'pointer' : 'default' }}>{label}</button>;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.94)', zIndex: 7000, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', color: '#fff' }} onClick={e => e.stopPropagation()}>
        <span style={{ fontSize: 14, fontWeight: 900 }}>{p.who || 'Someone'}</span>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>{when(p.when)} · {SOURCE[p.source] || 'Photo'}{p.where ? ` · ${p.where}` : ''}</span>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>· {index + 1} of {photos.length}</span>
        <span style={{ flex: 1 }} />
        <a href={p.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12.5, color: '#fff', fontWeight: 700 }}>Open full size ↗</a>
        <button onClick={onClose} aria-label="Close" style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 8, width: 32, height: 32, fontSize: 18, cursor: 'pointer' }}>×</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '0 12px' }}>
        {nav('‹', index - 1, index > 0)}
        <img src={p.url} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: 'calc(100% - 120px)', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }} />
        {nav('›', index + 1, index < photos.length - 1)}
      </div>
      {p.caption && <div onClick={e => e.stopPropagation()} style={{ padding: '10px 16px 16px', color: 'rgba(255,255,255,0.85)', fontSize: 13.5, textAlign: 'center' }}>{p.caption}</div>}
    </div>
  );
}

// "+ Add photos" on the strip (Ryan, Nov 2 2026: a PM wants to put pictures on a work order
// from the desktop). Files them exactly the way the phone's Add photos does — POST /upload,
// then a "Photos from the field" job note carrying the pictures (the caption is the note) — so
// they land beside the crew's pictures on every card and the job's Photos tile, and in the
// Notes tile and My Updates like any field photo. Up to 10 at a time (the upload route's cap).
function AddPhotos({ projectId, onAdded, tone = '#0369a1' }) {
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const previews = React.useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(u => URL.revokeObjectURL(u)), [previews]);
  function pick(e) {
    const list = Array.from(e.target.files || []).filter(f => /^image\//.test(f.type) || /\.(jpe?g|png|gif|webp|heic)$/i.test(f.name));
    e.target.value = '';
    if (!list.length) { setErr('Pick pictures (JPG, PNG, HEIC…).'); return; }
    setErr(list.length > 10 ? 'Ten at a time — the first ten are in.' : '');
    setFiles(list.slice(0, 10));
  }
  async function submit() {
    if (!files.length || busy) return;
    setBusy(true); setErr('');
    try {
      const token = getToken();
      const fd = new FormData(); files.forEach(f => fd.append('files', f));
      const up = await fetch(`${API_BASE}/upload`, { method: 'POST', headers: token ? { 'x-worker-token': token } : {}, body: fd });
      const upData = await up.json().catch(() => ({}));
      if (!up.ok || !Array.isArray(upData.urls) || !upData.urls.length) throw new Error(upData.error || 'Upload failed');
      await apiFetch(`/projects/${projectId}/notes`, { method: 'POST', body: JSON.stringify({ note: caption.trim() || 'Photos from the field', mediaUrls: upData.urls }) });
      setFiles([]); setCaption('');
      try { window.dispatchEvent(new Event('titan:documents-changed')); } catch { /* fine */ }
      if (onAdded) onAdded();
    } catch (e) { setErr(e?.error || e?.message || 'Could not add those — try again.'); }
    setBusy(false);
  }
  const small = { fontSize: 12, fontWeight: 700, borderRadius: 8, padding: '5px 10px', cursor: 'pointer' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
      {!files.length && (
        <label style={{ ...small, color: '#fff', background: tone, border: `1px solid ${tone}` }} title="Add pictures to this job — they show here and on the job's Photos tile, and go in with the field photos">
          ＋ Add photos
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={pick} />
        </label>
      )}
      {files.length > 0 && (
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', border: `1.5px solid ${tone}`, borderRadius: 10, padding: 10, width: 'min(420px, 100%)', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {previews.map((u, i) => <img key={i} src={u} alt="" style={{ width: 56, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />)}
          </div>
          <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="What are these? (optional — it prints as the note)" disabled={busy}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <button onClick={submit} disabled={busy} style={{ ...small, color: '#fff', background: tone, border: `1px solid ${tone}`, opacity: busy ? 0.6 : 1 }}>{busy ? 'Adding…' : `Add ${files.length} photo${files.length === 1 ? '' : 's'} to the job`}</button>
            <button onClick={() => { setFiles([]); setCaption(''); setErr(''); }} disabled={busy} style={{ ...small, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb' }}>Cancel</button>
            {err && <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>{err}</span>}
          </div>
        </div>
      )}
      {!files.length && err && <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>{err}</span>}
    </div>
  );
}

// The project page's Photos tile: the same strip, without the card border (the tile
// window is the card). Reloads when a note or card changes elsewhere on the page.
export function JobPhotosPillow({ projectId }) {
  const { photos, reload } = useJobPhotos(projectId);
  useEffect(() => { const h = () => reload(); window.addEventListener('titan:documents-changed', h); window.addEventListener('focus', h); return () => { window.removeEventListener('titan:documents-changed', h); window.removeEventListener('focus', h); }; }, [reload]);
  return <JobPhotosSection photos={photos} bare projectId={projectId} onAdded={reload} />;
}

// The strip itself. `photos` from useJobPhotos (null while loading). Newest first,
// thumbnails with who/when underneath; `max` trims the strip with a "show all".
// `projectId` + `onAdded` turn on the "+ Add photos" button (anyone signed in can add; the
// pictures file as a field-photo note on the job).
export function JobPhotosSection({ photos, tone = '#0369a1', toneSoft = '#f0f9ff', toneEdge = '#bae6fd', max = 24, bare = false, projectId = null, onAdded = null, emptyText = null }) {
  const empty = emptyText || (projectId ? 'No photos on this job yet. Add some here, or the crew adds them from the phone — Photos on the job, a T&M day, or a loop location.' : 'No photos on this job yet. The crew adds them from the phone — Photos on the job, a T&M day, or a loop location.');
  const [open, setOpen] = useState(null);
  const [all, setAll] = useState(false);
  const list = photos || [];
  const shown = all ? list : list.slice(0, max);
  return (
    <div style={bare ? {} : { border: `1.5px solid ${toneEdge}`, background: toneSoft, borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: tone }}>📷 Photos from the field{list.length ? ` · ${list.length}` : ''}</div>
        <span style={{ flex: 1 }} />
        {list.length > max && <button onClick={() => setAll(v => !v)} style={{ background: 'none', border: 'none', color: tone, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{all ? 'Show fewer' : `Show all ${list.length}`}</button>}
        {projectId && <AddPhotos projectId={projectId} onAdded={onAdded} tone={tone} />}
      </div>
      {photos == null ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading…</div>
        : list.length === 0 ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>{empty}</div>
        : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {shown.map((p, i) => (
              <button key={p.id} onClick={() => setOpen(i)} title={[p.who, when(p.when), p.caption].filter(Boolean).join(' — ')}
                style={{ width: 118, padding: 0, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', cursor: 'zoom-in', textAlign: 'left', overflow: 'hidden' }}>
                <img src={p.url} alt="" loading="lazy" style={{ width: 118, height: 92, objectFit: 'cover', display: 'block', background: '#f3f4f6' }} />
                <div style={{ padding: '5px 7px 6px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.who || 'Someone'}</div>
                  <div style={{ fontSize: 10.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{when(p.when)}{p.where ? ` · ${p.where}` : ''}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      {open != null && <Viewer photos={shown} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
    </div>
  );
}
